import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, ChevronRight, Search, X, Send, Loader2, Award } from 'lucide-react';
import { SeoHead } from '../components/SeoHead';
import { GUIDES, CATEGORIES, DIFFICULTY_COLOR, CATEGORY_COLOR } from './blog/guides';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('Tutti');
  const [query, setQuery] = useState('');
  const [certifiedBrands, setCertifiedBrands] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/superadmin/certified-brands`)
      .then(r => r.json())
      .then(d => setCertifiedBrands(d.certified_brands || []))
      .catch(() => {});
  }, []);

  // Integration Request Form state
  const [form, setForm] = useState({ brand: '', email: '', property_name: '', integration_type: 'smart-lock', notes: '' });
  const [formState, setFormState] = useState('idle'); // idle | loading | success | error
  const [formMsg, setFormMsg] = useState('');

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filtered = GUIDES.filter(g => {
    const matchCat = activeCategory === 'Tutti' || g.category === activeCategory || g.brand === activeCategory;
    const q = query.toLowerCase();
    const matchQ = !q || [g.title, g.brand, g.category, g.excerpt, ...(g.tags || [])].some(f => f.toLowerCase().includes(q));
    return matchCat && matchQ;
  });

  // ── Form submit ────────────────────────────────────────────────────────────
  const submitRequest = async e => {
    e.preventDefault();
    if (!form.brand.trim() || !form.email.trim()) { setFormMsg('Brand ed email sono obbligatori.'); setFormState('error'); return; }
    setFormState('loading');
    try {
      const res = await fetch(`${API}/api/demo/integration-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setFormState('success');
        setFormMsg(data.message || 'Richiesta ricevuta!');
        setForm({ brand: '', email: '', property_name: '', integration_type: 'smart-lock', notes: '' });
      } else {
        setFormState('error');
        setFormMsg(data.detail || 'Errore durante l\'invio. Riprova.');
      }
    } catch {
      setFormState('error');
      setFormMsg('Errore di rete. Verifica la connessione e riprova.');
    }
  };

  const inputStyle = { background: '#0c0c0c', border: '1px solid #242424', color: '#e5e5e5', fontSize: '13px', borderRadius: '2px', padding: '10px 14px', width: '100%', outline: 'none' };


  return (
    <div style={{ background: '#080808', color: '#e5e5e5', fontFamily: "'DM Sans', sans-serif", minHeight: '100vh' }}>
      <SeoHead page="blog" lang="it" theme="dark" />

      {/* Nav */}
      <div className="sticky top-0 z-40 border-b" style={{ background: 'rgba(8,8,8,0.96)', backdropFilter: 'blur(20px)', borderColor: '#1a1a1a' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '18px', fontWeight: 300, letterSpacing: '0.05em', color: '#e5e5e5' }}>Shangrilux</span>
            <ChevronRight size={13} style={{ color: 'rgba(229,229,229,0.3)' }} />
            <span className="text-sm" style={{ color: '#c9a84c' }}>Guide & Integrazioni</span>
          </a>
          <a href="/#demo" className="text-sm px-4 py-2 rounded-sm font-medium"
            style={{ background: '#c9a84c', color: '#000' }}>Demo Gratuita</a>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-14">
        <p className="text-xs tracking-[5px] uppercase mb-5" style={{ color: '#c9a84c' }}>Guide Tecniche</p>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 'clamp(36px,5vw,60px)', fontWeight: 300, lineHeight: 1.1, marginBottom: '16px' }}>
          Guide & Integrazioni.<br />
          <span style={{ color: '#c9a84c' }}>Tutto documentato.</span>
        </h1>
        <p className="max-w-xl text-base" style={{ color: 'rgba(229,229,229,0.5)', lineHeight: 1.7 }}>
          Guide complete per integrare serrature smart, sistemi di domotica, protocolli BACnet/KNX e qualsiasi dispositivo con la piattaforma Shangrilux.
        </p>

        {/* Stats */}
        <div className="flex flex-wrap gap-8 mt-10">
          {[
            { value: `${GUIDES.length}`, label: 'Guide disponibili' },
            { value: '18', label: 'Brand supportati' },
            { value: '3', label: 'Livelli di difficoltà' },
          ].map(s => (
            <div key={s.label}>
              <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '32px', fontWeight: 300, color: '#c9a84c', lineHeight: 1 }}>{s.value}</p>
              <p className="text-xs mt-1" style={{ color: 'rgba(229,229,229,0.4)' }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      <div className="border-y" style={{ borderColor: '#141414' }}>
        <div className="max-w-7xl mx-auto px-6 flex gap-2 overflow-x-auto py-4">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setActiveCategory(cat)}
              className="px-4 py-1.5 text-xs rounded-full shrink-0 transition-all"
              style={{
                background: activeCategory === cat ? '#c9a84c' : 'transparent',
                color: activeCategory === cat ? '#000' : 'rgba(229,229,229,0.5)',
                border: `1px solid ${activeCategory === cat ? '#c9a84c' : 'rgba(255,255,255,0.1)'}`,
                fontWeight: activeCategory === cat ? 600 : 400,
              }}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div className="max-w-7xl mx-auto px-6 pt-10 pb-2">
        <div className="relative max-w-xl" style={{ position: 'relative' }}>
          <Search size={15} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(229,229,229,0.3)', pointerEvents: 'none' }} />
          <input
            data-testid="blog-search-input"
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca brand, protocollo, categoria… (es. Nuki, MQTT, BACnet)"
            style={{
              background: '#0c0c0c',
              border: '1px solid #242424',
              color: '#e5e5e5',
              fontSize: '13px',
              borderRadius: '2px',
              padding: '11px 40px 11px 38px',
              width: '100%',
              outline: 'none',
            }}
            onFocus={e => { e.target.style.borderColor = '#c9a84c'; }}
            onBlur={e => { e.target.style.borderColor = '#242424'; }}
          />
          {query && (
            <button
              data-testid="blog-search-clear"
              onClick={() => setQuery('')}
              style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(229,229,229,0.4)', padding: 0, display: 'flex', alignItems: 'center' }}
            >
              <X size={14} />
            </button>
          )}
        </div>
        {query && (
          <p className="text-xs mt-2" style={{ color: 'rgba(229,229,229,0.35)' }} data-testid="blog-search-results-count">
            {filtered.length === 0
              ? 'Nessuna guida trovata per questa ricerca.'
              : `${filtered.length} guida${filtered.length === 1 ? '' : 'e'} trovata${filtered.length === 1 ? '' : 'e'} per "${query}"`}
          </p>
        )}
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(guide => {
            const diff = DIFFICULTY_COLOR[guide.difficulty] || DIFFICULTY_COLOR['Facile'];
            const catColor = CATEGORY_COLOR[guide.category] || '#c9a84c';
            const isCertified = certifiedBrands.includes(guide.brand);
            return (
              <a key={guide.slug} href={`/blog/${guide.slug}`}
                data-testid={`guide-card-${guide.slug}`}
                className="group block rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1"
                style={{ background: '#0c0c0c', border: `1px solid ${isCertified ? 'rgba(201,168,76,0.3)' : '#1a1a1a'}` }}>

                {/* Color banner */}
                <div className="h-1.5 w-full" style={{ background: isCertified ? '#c9a84c' : catColor, opacity: 0.7 }} />

                <div className="p-6">
                  {/* Category + brand + certified badge */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-[10px] font-semibold tracking-[3px] uppercase" style={{ color: catColor }}>
                      {guide.category}
                    </span>
                    <span style={{ color: 'rgba(229,229,229,0.2)' }}>·</span>
                    <span className="text-[10px]" style={{ color: 'rgba(229,229,229,0.4)' }}>{guide.brand}</span>
                    {isCertified && (
                      <span data-testid={`certified-badge-${guide.slug}`}
                        className="flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-auto"
                        style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.35)' }}>
                        <Award size={9} /> Certified
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h2 className="font-medium mb-3 leading-snug group-hover:text-[#c9a84c] transition-colors"
                    style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', fontWeight: 400, color: '#e5e5e5' }}>
                    {guide.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-xs leading-relaxed mb-5" style={{ color: 'rgba(229,229,229,0.45)' }}>
                    {guide.excerpt.slice(0, 110)}…
                  </p>

                  {/* Meta */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full"
                        style={{ background: diff.bg, color: diff.text, border: `1px solid ${diff.border}` }}>
                        {guide.difficulty}
                      </span>
                      <span className="text-[11px] flex items-center gap-1" style={{ color: 'rgba(229,229,229,0.3)' }}>
                        <Clock size={10} /> {guide.readTime}
                      </span>
                    </div>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform"
                      style={{ color: 'rgba(229,229,229,0.25)' }} />
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p style={{ color: 'rgba(229,229,229,0.3)' }}>Nessuna guida trovata per questa categoria.</p>
          </div>
        )}
      </div>

      {/* Integration Request Form */}
      <div className="border-t" style={{ borderColor: '#1a1a1a' }}>
        <div className="max-w-2xl mx-auto px-6 py-20">
          <p className="text-xs tracking-[4px] uppercase mb-4" style={{ color: '#c9a84c' }}>Non trovi la tua integrazione?</p>
          <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '32px', fontWeight: 300, color: '#e5e5e5', marginBottom: '12px' }}>
            Richiedila — la aggiungiamo.
          </h3>
          <p className="text-sm mb-10" style={{ color: 'rgba(229,229,229,0.45)', lineHeight: 1.7 }}>
            Shangrilux supporta 18+ brand. Nuove integrazioni vengono aggiunte ogni mese. Compilami il form qui sotto — il team tecnico ti risponde entro 48 ore.
          </p>

          {formState === 'success' ? (
            <div
              data-testid="integration-form-success"
              className="rounded-sm p-8 text-center"
              style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.25)' }}
            >
              <p className="font-medium mb-2" style={{ color: '#059669' }}>Richiesta inviata con successo.</p>
              <p className="text-sm" style={{ color: 'rgba(229,229,229,0.5)' }}>{formMsg}</p>
              <button
                onClick={() => { setFormState('idle'); setFormMsg(''); }}
                className="mt-5 text-xs underline"
                style={{ color: 'rgba(229,229,229,0.4)', background: 'none', border: 'none', cursor: 'pointer' }}
              >Invia un'altra richiesta</button>
            </div>
          ) : (
            <form onSubmit={submitRequest} data-testid="integration-request-form" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Brand */}
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'rgba(229,229,229,0.45)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                    Brand / Dispositivo <span style={{ color: '#c9a84c' }}>*</span>
                  </label>
                  <input
                    data-testid="form-brand"
                    type="text"
                    required
                    placeholder="es. Dormakaba EVOline, Siemens DESIGO"
                    value={form.brand}
                    onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                {/* Email */}
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'rgba(229,229,229,0.45)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                    Email <span style={{ color: '#c9a84c' }}>*</span>
                  </label>
                  <input
                    data-testid="form-email"
                    type="email"
                    required
                    placeholder="tua@email.it"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                {/* Property name */}
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'rgba(229,229,229,0.45)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                    Nome Struttura
                  </label>
                  <input
                    data-testid="form-property-name"
                    type="text"
                    placeholder="Hotel / Villa / B&B"
                    value={form.property_name}
                    onChange={e => setForm(f => ({ ...f, property_name: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                {/* Integration type */}
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'rgba(229,229,229,0.45)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                    Tipo di Integrazione
                  </label>
                  <select
                    data-testid="form-integration-type"
                    value={form.integration_type}
                    onChange={e => setForm(f => ({ ...f, integration_type: e.target.value }))}
                    style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
                  >
                    <option value="smart-lock">Serratura Smart</option>
                    <option value="building-automation">Building Automation (KNX/BACnet)</option>
                    <option value="pms">PMS / Gestionale</option>
                    <option value="access-control">Controllo Accessi</option>
                    <option value="iot-device">Dispositivo IoT / MQTT</option>
                    <option value="other">Altro</option>
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-5">
                <label className="block text-xs mb-1.5" style={{ color: 'rgba(229,229,229,0.45)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  Note / Modello specifico
                </label>
                <textarea
                  data-testid="form-notes"
                  rows={3}
                  placeholder="Descrivi il modello specifico, il protocollo usato, o eventuali requisiti particolari..."
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                />
              </div>

              {formState === 'error' && formMsg && (
                <p data-testid="integration-form-error" className="text-xs mb-4 px-3 py-2 rounded-sm"
                  style={{ color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  {formMsg}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4">
                <button
                  data-testid="integration-form-submit"
                  type="submit"
                  disabled={formState === 'loading'}
                  className="flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-sm transition-opacity"
                  style={{ background: '#c9a84c', color: '#000', opacity: formState === 'loading' ? 0.7 : 1, cursor: formState === 'loading' ? 'not-allowed' : 'pointer', border: 'none' }}
                >
                  {formState === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {formState === 'loading' ? 'Invio in corso…' : 'Invia Richiesta'}
                </button>
                <a href="/#demo" className="text-sm" style={{ color: 'rgba(229,229,229,0.4)' }}>
                  oppure <span style={{ color: '#c9a84c' }}>richiedi una demo →</span>
                </a>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t py-10" style={{ borderColor: '#141414' }}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4">
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '16px', fontWeight: 300, color: 'rgba(229,229,229,0.3)' }}>Shangrilux</span>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/', 'Home'], ['/pricing', 'Prezzi'], ['/marketplace', 'Marketplace'], ['/developers', 'API']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.3)' }}>{l}</a>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/privacy', 'Privacy'], ['/cookie', 'Cookie'], ['/terms', 'Termini']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.25)' }}>{l}</a>
            ))}
          </div>
          <p className="text-xs" style={{ color: 'rgba(229,229,229,0.2)' }}>
            © {new Date().getFullYear()} Shangrilux · Design by{' '}
            <a href="https://francescorusso.work" target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(201,168,76,0.5)' }}>Francesco Russo</a>
            {' · '}
            <a href="https://shangrilux.com" target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(201,168,76,0.5)' }}>Shangrilux</a>
          </p>
        </div>
      </div>
    </div>
  );
}
